import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, OrderKindBO, sortVatRates, VatRateForSaleBaseQuery, VatRateItem } from '@skylab-shared/business';
import { EventBusBase, IdentifiableValueConverter } from '@skylab-shared/core';
import { LabeledEnumValue, LabeledEnumValueConverter, OrderDocumentKind, OrderKindModel, SaleDocumentKind } from '@skylab-shared/dataModel';

import { OrderKindRepository, OrderKindRepositoryBase } from '@skylab-angular-shared/business';
import { CommandBus, EnumTranslator, EnumTranslatorBase, EventBus, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, FocusByDirective, FormContainerComponent, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface OrderKindDialogResult {
    id: number;
    version: string;
}

interface OrderKindDialogViewModel extends DictionaryDialogViewModelBase<OrderKindBO> {
    documentName: string;
    focusReady: boolean;
    documentKindConverter: LabeledEnumValueConverter<SaleDocumentKind>;
    documentKinds: LabeledEnumValue[];
    vatRates: VatRateItem[];
    vatRateValueConverter: IdentifiableValueConverter;
    vatRateVisible: boolean;
}

@Component({
    selector: 'skl-order-kind-dialog',
    templateUrl: './order-kind-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, FocusByDirective, TextValueAccessorDirective, FormsModule, ValidateDirective, ButtonComponent, TranslatePipe, SelectValueAccessorDirective]
})
export class OrderKindDialogComponent extends DictionaryDialogComponentBase<OrderKindModel, OrderKindBO, OrderKindRepositoryBase, OrderKindDialogViewModel, DictionaryDialogData<string>> {
    public constructor(
        @Inject(OrderKindRepository) repo: OrderKindRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        toastService: ToastService,
        commandBus: CommandBus,

        dialogService: DialogInsService,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase
    ) {
        super(repo, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override getInitialState(): OrderKindDialogViewModel {
        const documentKinds = this.enumTranslator.getLabeledEnumValueArray(SaleDocumentKind).filter(e => [SaleDocumentKind.Invoice, SaleDocumentKind.Receipt].includes(e.value as any));
        return {
            ...super.getInitialState(),
            documentKindConverter: new LabeledEnumValueConverter(this.enumTranslator, SaleDocumentKind),
            documentKinds,
            documentName: '',
            vatRateVisible: false
        };
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            super.preloadDataAsync(),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(VatRateForSaleBaseQuery),
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();
        this.viewModel.documentName = this.enumTranslator.getLabeledEnumValue(OrderDocumentKind, this.viewModel.bo.model.name).label;
        this.viewModel.vatRates = sortVatRates(this.localContext.getPreloaded(VatRateForSaleBaseQuery) || []);
        this.viewModel.vatRateValueConverter = new IdentifiableValueConverter(this.viewModel.vatRates);
        this.viewModel.vatRateVisible = !!this.localContext.getPreloaded(CompanyParametersQuery)?.vatPayer;
    }

    protected override get dictionaryToLoadProperties(): { id: number | null, [key: string]: string | number | null } | null {
        return this.data?.id ? { id: null, name: this.data.id } : null;
    }
}
